import type { LegendaLang } from './vocabulary';

/**
 * Metres per pixel of bar. One scale, used everywhere an uncertainty appears —
 * table, map, 3D and export — so two rows can be compared without reading
 * either number. Changing it changes every surface at once, which is the point.
 */
const METRES_PER_PIXEL = 1.8;
const MAX_BAR_PX = 72;

export interface UncertaintyIndicatorProps {
  /** One-sigma positional uncertainty in metres, or null when unknown. */
  metres: number | null;
  lang: LegendaLang;
}

const NO_VALUE: Record<LegendaLang, string> = {
  ru: 'погрешность не установлена',
  hy: 'սխալանքը սահմանված չէ',
  en: 'uncertainty not established',
};

/**
 * Uncertainty as a quantity, not a mood.
 *
 * A collar at ±30 m and one at ±100 m are different facts, so the bar's length
 * is linear in metres rather than a category or a colour ramp. On the map the
 * same figure becomes a ring drawn at map scale — which is what stops a reader
 * believing any of these positions is drilled-ready.
 *
 * A null value renders as a stated absence, never as zero. Rendering unknown
 * uncertainty as a short bar would claim precision the archive never had.
 */
export default function UncertaintyIndicator({ metres, lang }: UncertaintyIndicatorProps) {
  if (metres === null) {
    return <span className="lg-unc lg-muted">{NO_VALUE[lang]}</span>;
  }

  const width = Math.min(MAX_BAR_PX, Math.max(4, metres / METRES_PER_PIXEL));

  return (
    <span className="lg-unc" aria-label={`±${metres} m`}>
      <span className="lg-unc__bar" style={{ width: `${width}px` }} aria-hidden="true" />±{metres} м
    </span>
  );
}
